<template>
  <div class="px-2">
    <h1 class="text-xs-center">
      {{ isNewEvent ? 'Create Event' : (currentEvent && currentEvent.dateArchived ? 'Archived Event' : 'Edit Event') }}
    </h1>
    <div class="layout justify-end mb-3">
      <v-btn
        color="warning" flat
        @click="$router.push({ name: 'routes-events' })"
        :disabled="apiInProgress && deleteInProgress && unarchiveInProgress && metricsLoading"
      >
        Cancel
      </v-btn>
      <v-btn
        color="error"
        flat
        v-if="currentEvent && !currentEvent.dateArchived"
        :loading="deleteInProgress"
        :disabled="!isEditable || isOnlyInvestor"
        @click="onArchive"
      >
        <v-icon left small>fas fa-archive</v-icon>
        Archive
      </v-btn>
      <v-btn
        v-if="currentEvent && currentEvent.dateArchived"
        :loading="unarchiveInProgress"
        :disabled="(!isEditable && (currentEvent && !currentEvent.dateArchived)) || isOnlyInvestor"
        @click="onUnarchive"
      >
        <v-icon left small>fas fa-undo</v-icon>
        Unarchive
      </v-btn>
      <v-btn
        v-if="currentEvent && !currentEvent.dateArchived"
        color="primary"
        :loading="apiInProgress"
        :disabled="!isEditable || isOnlyInvestor"
        @click="onSave"
      >
        Save
      </v-btn>
    </div>
    <v-layout row wrap class="mb-3">
      <v-flex xs12 md8 offset-md2>
        <div>
          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Event name
            </v-flex>
            <v-flex sm8>
              <v-text-field
                required
                placeholder="Enter event name"
                v-model="currentEvent.name"
                :disabled="!isEditable"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Event date
            </v-flex>
            <v-flex sm8>
              <div style="max-width: 300px">
                <v-menu
                  v-model="eventDateMenu"
                  :disabled="!isEditable"
                  close-on-content-click
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      prepend-icon="fal fa-calendar"
                      :value="computedEventDateFormatted"
                      placeholder="Select event date"
                      readonly
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="eventDateLocal"
                    @change="onChangeEventDate"
                    no-title
                  />
                </v-menu>
              </div>
            </v-flex>
          </v-layout>
          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Event times/stops
            </v-flex>
            <v-flex sm8>
              <routes-events-bus-stop-pickup-times
                :pickup-times="currentEvent && currentEvent.busStopPickupTimes"
                :is-admin="isAdmin"
                :disabled="!isEditable"
                :on-change="onPickupTimesChange"
                @add="openNewPickupTimeDialog"
                @edit="openEditPickupTimeDialog"
                @delete="onDeletePickupTime"
              />
            </v-flex>
          </v-layout>

          <v-layout row wrap mb-1>
            <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
              Flat rate fee
            </v-flex>
            <v-flex sm8>
              <currency-field
                :child-props="{ placeholder: 'Amount', disabled: !isEditable }"
                v-model="currentEvent.flatRateFee"
              />
            </v-flex>
          </v-layout>
        </div>

        <v-card class="mt-3">
          <v-card-text>
            <v-layout row wrap mb-3>
              <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                Return bus stop
              </v-flex>
              <v-flex sm8>
                <routes-events-bus-stop-pickup-times
                  :pickup-times="currentEvent && currentEvent.returnBusStops"
                  :is-admin="isAdmin"
                  bus-stop-only
                  :disabled="!isEditable"
                  @add="openNewReturnBusStopDialog"
                  @edit="openEditReturnBusStopDialog"
                  @delete="onDeleteReturnBusStop"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap mb-3>
              <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                Return event time(s)
              </v-flex>
              <v-flex sm8>
                <routes-events-bus-stop-pickup-times
                  :pickup-times="currentEvent && currentEvent.returnPickupTimes"
                  :is-admin="isAdmin"
                  :disabled="!isEditable"
                  time-only
                  @add="openNewReturnPickupTimeDialog"
                  @edit="openEditReturnPickupTimeDialog"
                  @delete="onDeleteReturnPickupTime"
                />
              </v-flex>
            </v-layout>
            <v-layout row wrap mb-1>
              <v-flex class="text-xs-right font-weight-bold" sm4 mt-3 pr-3>
                Return flat rate fee
              </v-flex>
              <v-flex sm8>
                <currency-field
                  :child-props="{ placeholder: 'Amount', disabled: !isEditable }"
                  v-model="currentEvent.returnFlatRateFee"
                />
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>

        <v-card class="mt-3">
          <v-card-title>
            <strong>Capacity &amp; Attached Vehicles</strong>
            <v-spacer />
            <v-btn
              small
              icon
              v-if="!isNewEvent"
              :loading="metricsLoading"
              :disabled="metricsLoading || !isEditable"
              @click="refreshMetrics"
              title="Refresh capacity and vehicles"
            >
              <v-icon small>fal fa-sync</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <div v-if="metrics">
              <p class="mb-1">
                <strong>Outbound capacity (reserved / total):</strong>
                {{ metrics.ticketsSold }} / {{ metrics.totalCapacity }}
                <span class="grey--text text--darken-1">({{ metrics.available }} available)</span>
              </p>
              <p class="mb-1">
                <strong>Return capacity (reserved / total):</strong>
                <span v-if="(currentEvent && currentEvent.returnFlatRateFee !== null && currentEvent.returnFlatRateFee !== undefined) || (currentEvent && Array.isArray(currentEvent.returnPickupTimes) && currentEvent.returnPickupTimes.length)">
                  <span v-if="metrics.returnTicketsSold !== undefined && metrics.returnTicketsSold !== null">
                    {{ metrics.returnTicketsSold }} / {{ metrics.totalReturnCapacity }}
                    <span class="grey--text text--darken-1">({{ metrics.returnsAvailable }} available)</span>
                  </span>
                  <span v-else>—</span>
                </span>
                <span v-else class="grey--text">N/A</span>
              </p>
            </div>
            <div v-else>
              <p>No capacity data loaded yet.</p>
            </div>

            <div class="mt-2">
              <div>
                <strong>Attached Vehicles:</strong>
                <v-btn small :disabled="!isEditable" @click="manageVehiclesDialog = true">
                  <v-icon small left>fas fa-bus</v-icon> Manage attached vehicles
                </v-btn>
              </div>
              <div>
                <div v-if="currentEvent && currentEvent.transportationProfiles">
                  <div class="mt-1">
                    <div v-if="currentEvent.transportationProfiles && currentEvent.transportationProfiles.length">
                      <v-chip
                        v-for="profile in currentEvent.transportationProfiles"
                        :key="profile.id"
                        small
                        class="ma-1"
                      >
                        ({{ profile.shortcodeId }}) {{ profile.licensePlateNumber }}
                      </v-chip>
                    </div>
                    <div v-else>
                      <span>No attached vehicles</span>
                    </div>
                  </div>
                </div>
                <div v-else>—</div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-dialog
          v-model="manageVehiclesDialog"
          persistent
          max-width="700"
        >
          <v-card>
            <v-card-title class="headline grey lighten-2">
              Manage Attached Vehicles
              <v-spacer />
              <v-btn icon @click="onManageVehiclesCancel">
                <v-icon>fal fa-times</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <transportation-profile-selector
                :initial-selected-profile-ids="transportationProfileIds"
                :result-limit="20"
                :disabled="apiInProgress || deleteInProgress || !isAdmin"
                @submit="onManageVehiclesSubmit"
                @cancel="onManageVehiclesCancel"
              />
            </v-card-text>
          </v-card>
        </v-dialog>
        <routes-events-bus-stop-pickup-times-dialog
          :open-modal="manageBusStopTimesDialog"
          :is-admin="isAdmin"
          :is-new-event="isNewEvent"
          :pickup-times="currentEvent && currentEvent.busStopPickupTimes"
          :editing-index="editingPickupIndex"
          :on-modal-close="() => { manageBusStopTimesDialog = false; editingPickupIndex = -1 }"
          :on-change="onPickupTimesChange"
        />
        <routes-events-bus-stop-pickup-times-dialog
          time-only
          :open-modal="manageReturnPickupTimesDialog"
          :is-admin="isAdmin"
          :is-new-event="isNewEvent"
          :pickup-times="currentEvent && currentEvent.returnPickupTimes"
          :editing-index="editingReturnPickupIndex"
          :on-modal-close="() => { manageReturnPickupTimesDialog = false; editingReturnPickupIndex = -1 }"
          :on-change="onReturnPickupTimesChange"
        />
        <routes-events-bus-stop-pickup-times-dialog
          bus-stop-only
          :open-modal="manageReturnBusStopDialog"
          :is-admin="isAdmin"
          :is-new-event="isNewEvent"
          :pickup-times="currentEvent && currentEvent.returnBusStops"
          :editing-index="editingReturnBusStopIndex"
          :on-modal-close="() => { manageReturnBusStopDialog = false; editingReturnBusStopIndex = -1 }"
          :on-change="onReturnBusStopChange"
        />
      </v-flex>
    </v-layout>

    <div class="layout justify-end mb-3">
      <v-btn
        color="warning" flat
        @click="$router.push({ name: 'routes-events' })"
        :disabled="apiInProgress && deleteInProgress && unarchiveInProgress && metricsLoading"
      >
        Cancel
      </v-btn>
      <v-btn
        color="error"
        flat
        v-if="currentEvent && !currentEvent.dateArchived"
        :loading="deleteInProgress"
        :disabled="!isEditable || isOnlyInvestor"
        @click="onArchive"
      >
        <v-icon left small>fas fa-archive</v-icon>
        Archive
      </v-btn>
      <v-btn
        v-if="currentEvent && currentEvent.dateArchived"
        :loading="unarchiveInProgress"
        :disabled="(!isEditable && (currentEvent && !currentEvent.dateArchived)) || isOnlyInvestor"
        @click="onUnarchive"
      >
        <v-icon left small>fas fa-undo</v-icon>
        Unarchive
      </v-btn>
      <v-btn
        v-if="currentEvent && !currentEvent.dateArchived"
        color="primary"
        :loading="apiInProgress"
        :disabled="!isEditable || isOnlyInvestor"
        @click="onSave"
      >
        Save
      </v-btn>
    </div>
  </div>
</template>

<script>
import './components/bus-stop-pickup-times'
import './components/bus-stop-pickup-times-dialog'
import src from './src'
export default src
</script>
